import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { userService } from '@/services/user_service'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const schema = z
  .object({
    old_password: z.string().min(1, '請輸入舊密碼'),
    new_password: z.string().min(6, '密碼至少需要6個字元'),
    new_password_confirm: z.string().min(6, '密碼至少需要6個字元'),
  })
  .refine((v) => v.new_password === v.new_password_confirm, {
    message: '新密碼不一致',
    path: ['new_password_confirm'],
  })

export default function ChangePasswordDialog({ open, onOpenChange }: Props) {
  const { token } = useAuth()

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      old_password: '',
      new_password: '',
      new_password_confirm: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof schema>) => {
    try {
      const resp = await userService.changeMyPassword(values, token || '')
      if (!resp.success) {
        const msg = resp.error?.message || '變更密碼失敗'
        // 映射常見錯誤到欄位或全域錯誤
        if (resp.error?.code === 'OLD_PASSWORD_INVALID') {
          form.setError('old_password', { message: '舊密碼不正確' })
        } else if (resp.error?.code === 'PASSWORD_MISMATCH') {
          form.setError('new_password_confirm', { message: '新密碼不一致' })
        } else if (resp.error?.code === 'PASSWORD_TOO_SHORT') {
          form.setError('new_password', { message: '密碼至少需要6個字元' })
        } else {
          form.setError('root', { message: msg })
        }
        return
      }
      form.reset()
      onOpenChange(false)
    } catch {
      form.setError('root', { message: '變更密碼失敗' })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        form.clearErrors()
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>修改密碼</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 py-2">
            <FormField
              control={form.control}
              name="old_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>舊密碼</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>新密碼</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new_password_confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>確認新密碼</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <div className="text-sm text-red-500">{form.formState.errors.root.message}</div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={form.formState.isSubmitting}
              >
                取消
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? '處理中...' : '確認修改'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
